const VIDEO_SAVED_MODAL_ID = 'video-saved-modal-overlay';

export interface SavedVideoConfirmation {
  fileHandle: FileSystemFileHandle;
  fileName: string;
}

export function showVideoSavedModal(confirmation: SavedVideoConfirmation): void {
  if (document.getElementById(VIDEO_SAVED_MODAL_ID)) {
    return;
  }

  const overlay = document.createElement('div');
  overlay.id = VIDEO_SAVED_MODAL_ID;
  overlay.innerHTML = `
    <section
      class="video-saved-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="video-saved-modal-title"
      aria-describedby="video-saved-modal-message"
    >
      <button
        class="video-saved-modal-close-icon"
        type="button"
        aria-label="Close"
        data-video-saved-close
      >×</button>
      <div class="video-saved-modal-icon" aria-hidden="true">
        <svg viewBox="0 0 64 64" role="presentation">
          <path d="M18 33.5 27.5 43 47 22.5" />
        </svg>
      </div>
      <div class="video-saved-modal-copy">
        <h2 id="video-saved-modal-title">Export complete</h2>
        <p id="video-saved-modal-message"></p>
        <p class="video-saved-modal-error" role="alert" aria-live="polite"></p>
      </div>
      <div class="video-saved-modal-actions">
        <button class="video-saved-modal-watch" type="button">View video</button>
        <button class="video-saved-modal-close" type="button" data-video-saved-close>Close</button>
      </div>
    </section>
  `;

  const modal = overlay.querySelector<HTMLElement>('.video-saved-modal');
  const message = overlay.querySelector<HTMLElement>('#video-saved-modal-message');
  const watchButton = overlay.querySelector<HTMLButtonElement>('.video-saved-modal-watch');
  const errorLabel = overlay.querySelector<HTMLElement>('.video-saved-modal-error');
  let closing = false;

  if (message) {
    message.textContent = `Video “${confirmation.fileName}” was saved successfully.`;
  }

  const close = (): void => {
    if (closing) {
      return;
    }
    closing = true;
    overlay.classList.add('is-closing');
    document.removeEventListener('keydown', onKeyDown);
    window.setTimeout(() => overlay.remove(), 180);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      close();
    }
  };

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      close();
    }
  });
  modal?.addEventListener('click', (event) => event.stopPropagation());
  overlay.querySelectorAll<HTMLElement>('[data-video-saved-close]').forEach((button) => {
    button.addEventListener('click', close);
  });
  watchButton?.addEventListener('click', async () => {
    if (!watchButton || !errorLabel) {
      return;
    }

    errorLabel.textContent = '';
    const previewWindow = window.open('about:blank', '_blank');
    if (!previewWindow) {
      errorLabel.textContent =
        'The video could not be opened. Allow pop-ups and try again.';
      return;
    }

    watchButton.disabled = true;
    previewWindow.opener = null;
    try {
      const savedFile = await confirmation.fileHandle.getFile();
      if (savedFile.size === 0) {
        throw new Error('Saved video file is empty.');
      }
      const previewUrl = URL.createObjectURL(savedFile);
      previewWindow.document.title = confirmation.fileName;
      previewWindow.location.replace(previewUrl);
      window.setTimeout(() => URL.revokeObjectURL(previewUrl), 6 * 60 * 60 * 1000);
    } catch (error) {
      previewWindow.close();
      console.error('[VideoExport] Failed to open the saved video file.', error);
      errorLabel.textContent =
        'The saved video could not be opened. Please try again.';
    } finally {
      watchButton.disabled = false;
    }
  });

  document.addEventListener('keydown', onKeyDown);
  document.body.appendChild(overlay);
  window.requestAnimationFrame(() => watchButton?.focus());
}
